import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, Grid3x3, Users, Image, Calendar, Mail, Handshake,
  LogOut, ChevronLeft, ChevronRight, Shield, Menu, X, Settings, Layers, KeyRound, Star, Sparkles, Briefcase, FileText, BarChart3
} from 'lucide-react';
import { verifyAdmin } from '../../services/api';
import { toast } from 'sonner';

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await verifyAdmin();
        setUser(res.data);
      } catch {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Logged out');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'admin', 'viewer'] },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', roles: ['super_admin', 'admin', 'viewer'] },
    { path: '/admin/products', icon: Package, label: 'Products', roles: ['super_admin', 'admin'] },
    { path: '/admin/categories', icon: Grid3x3, label: 'Categories', roles: ['super_admin', 'admin'] },
    { path: '/admin/subcategories', icon: Layers, label: 'Subcategories', roles: ['super_admin', 'admin'] },
    { path: '/admin/clients', icon: Users, label: 'Clients', roles: ['super_admin', 'admin'] },
    { path: '/admin/banners', icon: Image, label: 'Banners', roles: ['super_admin', 'admin'] },
    { path: '/admin/events', icon: Calendar, label: 'Events', roles: ['super_admin', 'admin'] },
    { path: '/admin/enquiries', icon: Mail, label: 'Enquiries', roles: ['super_admin', 'admin', 'viewer'] },
    { path: '/admin/reviews', icon: Star, label: 'Reviews', roles: ['super_admin', 'admin'] },
    { path: '/admin/dealerships', icon: Handshake, label: 'Dealerships', roles: ['super_admin', 'admin', 'viewer'] },
    { path: '/admin/users', icon: Shield, label: 'Users', roles: ['super_admin'] },
    { path: '/admin/settings', icon: Settings, label: 'Settings', roles: ['super_admin', 'admin'] },
    { path: '/admin/festival', icon: Sparkles, label: 'Festival Effects', roles: ['super_admin', 'admin'] },
    { path: '/admin/careers', icon: Briefcase, label: 'Careers', roles: ['super_admin', 'admin'] },
    { path: '/admin/case-studies', icon: FileText, label: 'Case Studies', roles: ['super_admin', 'admin'] },
    { path: '/admin/change-password', icon: KeyRound, label: 'Change Password', roles: ['super_admin', 'admin', 'viewer'] },
  ];

  const filteredNav = navItems.filter(item => !user || item.roles.includes(user.role));

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2E3A6F] border-t-transparent rounded-full" />
      </div>
    );
  }

  const SidebarContent = () => (
    <>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold flex-shrink-0">
            N
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <h2 className="text-white font-bold text-sm truncate">Namoh Toolings</h2>
              <p className="text-white/50 text-xs">CMS Admin</p>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNav.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-white/20 text-white shadow-sm'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`
            }
          >
            <item.icon size={18} className="flex-shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className={`flex items-center gap-3 px-3 py-2 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user.username?.charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-medium truncate">{user.username}</p>
              <p className="text-white/40 text-[10px] capitalize">{user.role?.replace('_', ' ')}</p>
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-300 hover:text-red-200 hover:bg-red-500/20 transition-all w-full mt-1 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-gradient-to-b from-[#1a1f3a] to-[#2E3A6F] transition-all duration-300 fixed h-screen z-40 ${
          collapsed ? 'w-[68px]' : 'w-60'
        }`}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-[#2E3A6F] border-2 border-white/20 flex items-center justify-center text-white hover:bg-[#1a1f3a] transition-colors"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#1a1f3a] z-50 flex items-center px-4 gap-3">
        <button onClick={() => setMobileOpen(true)} className="text-white">
          <Menu size={22} />
        </button>
        <span className="text-white font-bold text-sm">Namoh CMS</span>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-gradient-to-b from-[#1a1f3a] to-[#2E3A6F] flex flex-col">
            <div className="flex justify-end p-2">
              <button onClick={() => setMobileOpen(false)} className="text-white/60 hover:text-white p-1">
                <X size={20} />
              </button>
            </div>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-[68px]' : 'lg:ml-60'} pt-14 lg:pt-0`}>
        <div className="p-4 lg:p-6 min-h-screen">
          <Outlet context={{ user }} />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
